// NodeMoneyModal.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import {
  formatCurrencyDollarsString,
  isCurrencyInputPartial,
  parseCurrencyInputToCents,
  resolveFieldCurrencyCode,
} from 'utilities/Currency';

// CSS
import './NodeMoneyModal.css';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';
import TextInput from 'components/textinput/TextInput';

/**
 * NodeMoneyModal
 *
 * Modal for editing a schema-driven money field. Displays major units and
 * saves minor units (cents), or '' when cleared.
 *
 * @param {string} [label] - Modal title / field label.
 * @param {*} value - Current field value (minor units / cents).
 * @param {object|null} [field] - Schema field (currencyCode).
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {Function} setOpen - Setter for modal open state.
 * @param {Function} onChange - Callback with cents number or ''.
 *
 * @returns {ReactNode} NodeMoneyModal.
 */
const NodeMoneyModal = ({
  label,
  value,
  field = null,
  isOpen,
  setOpen,
  onChange,
}) => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const [draftValue, setDraftValue] = useState('');

  const currencyCode = useMemo(() => resolveFieldCurrencyCode(field), [field]);
  const displayValue = useMemo(
    () => formatCurrencyDollarsString(value, currencyCode),
    [currencyCode, value],
  );
  const modalTitle = useMemo(() => label || 'Money', [label]);

  useEffect(() => {
    if (isOpen) {
      setDraftValue(displayValue);
    }
  }, [displayValue, isOpen]);

  const handleChange = useCallback((event) => {
    const nextValue = event.target.value;
    if (nextValue === '') {
      setDraftValue('');
      return;
    }
    if (isCurrencyInputPartial(nextValue, currencyCode)) {
      setDraftValue(nextValue);
    }
  }, [currencyCode]);

  const handleSave = useCallback(() => {
    const nextText = String(draftValue ?? '').trim();
    if (nextText === '') {
      onChange?.('');
      setOpen(false);
      return;
    }

    const parsed = parseCurrencyInputToCents(nextText, currencyCode);
    if (!parsed.ok) {
      setDraftValue(displayValue);
      return;
    }

    onChange?.(parsed.cents);
    setOpen(false);
  }, [currencyCode, displayValue, draftValue, onChange, setOpen]);

  return (
    <Modal
      title={modalTitle}
      isOpen={isOpen}
      width={isMobile ? '100%' : 420}
      height="auto"
      onClose={() => setOpen(false)}
    >
      <ModalForm>
        <div className="node-money-modal-wrapper">
          <TextInput
            value={draftValue}
            placeholder="Set amount..."
            inputMode="decimal"
            startIconKey="attach_money"
            startIconColor={theme.foregroundMuted}
            iconSize={22}
            hybrid
            autoFocus
            onChange={handleChange}
          />
        </div>
      </ModalForm>

      <ModalButtons>
        <Button label="Cancel" onClick={() => setOpen(false)} />
        <Button label="Save" onClick={handleSave} />
      </ModalButtons>
    </Modal>
  );
};

NodeMoneyModal.displayName = 'NodeMoneyModal';

export default NodeMoneyModal;
